<template>
  <div
    class="basic-button"
    :class="{
      'basic-button--inline': inline,
    }"
    :data-private-info="true"
  >
    <var-style-provider :style-vars="styleVars">
      <var-button
        class="basic-button__button"
        :class="{
          'basic-button--full-width': fullWidth,
          [`basic-button--padding-${props.padding}`]: props.padding,
        }"
        :outline="outline"
        :type="varButtonType"
        :native="nativeType"
        :text="text"
        :disabled="disabled"
        :block="fullWidth"
        @click="(e) => emit('buttonClick', e)"
      >
        <template v-if="label">{{ label }}</template>
        <slot v-else></slot>
      </var-button>
    </var-style-provider>
  </div>
</template>

<script lang="ts" setup>
import './BasicButton.scss';
import { LDTheme } from 'ewi-ui-helpers/types';

export type ButtonType = 'filled' | 'outlined' | 'elevated' | 'text' | 'tonal';
export type ButtonPadding = 'none' | 'small' | 'medium' | 'large';
export interface BasicButtonProps {
  buttonType?: ButtonType;
  nativeType?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  form?: string;
  label?: string;
  theme?: LDTheme;
  loading?: boolean;
  fullWidth?: boolean;
  inline?: boolean;
  padding?: ButtonPadding;
}

defineOptions({
  name: 'BasicButton',
});

const props = withDefaults(defineProps<BasicButtonProps>(), {
  buttonType: 'filled',
  theme: 'light',
  nativeType: 'button',
  loading: false,
  inline: false,
  noPadding: false,
});

const outline = computed(() => props.buttonType === 'outlined');
const text = computed(() => props.buttonType === 'text');
const varButtonType = computed(() => {
  switch (props.buttonType) {
    case 'filled':
      return 'primary';
    case 'tonal':
      return 'danger';
    case 'elevated':
      return 'info';
    default:
      return 'default';
  }
});

const emit = defineEmits<{
  (e: 'buttonClick', event: Event): void;
}>();

const lightTheme = {
  '--button-default-color': 'var(--SysLightPrimary)',
  '--button-primary-color': 'var(--SysLightPrimary)',
  '--button-danger-color': 'var(--SysLightSecondaryContainer)',
  '--button-info-color': 'var(--SysLightSurface)',
  '--button-outlined-color': 'none',

  '--button-default-text-color': 'var(--SysLightPrimary)',
  '--button-primary-text-color': 'var(--SysLightOnPrimary)',
  '--button-danger-text-color': 'var(--SysLightOnSecondaryContainer)',
  '--button-info-text-color': 'var(--SysLightPrimary)',
  '--text-button-text-color': 'var(--SysLightPrimary)',
  '--button-outlined-text-color': 'var(--SysLightPrimary)',
};

const darkTheme = {
  '--button-default-color': 'var(--SysDarkPrimary)',
  '--button-primary-color': 'var(--SysDarkPrimary)',
  '--button-danger-color': 'var(--SysDarkSecondaryContainer)',
  '--button-info-color': 'var(--SysDarkSurface)', // we do not have the token for the elevated button SysDarkSurface1
  '--button-disabled-color': 'var(--SysDarkOnSurface)',
  '--button-outlined-color': 'none',

  '--button-default-text-color': 'var(--SysDarkPrimary)',
  '--button-primary-text-color': 'var(--SysDarkOnPrimary)',
  '--button-danger-text-color': 'var(--SysDarkOnSecondaryContainer)',
  '--button-info-text-color': 'var(--SysDarkPrimary)',
  '--text-button-text-color': 'var(--SysDarkPrimary)',
  '--button-outlined-text-color': 'var(--SysDarkPrimary)',
};

const styleVars = ref(props.theme === 'light' ? lightTheme : darkTheme);
</script>
